import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone, MessageCircle, CheckCircle, Headphones, Clock, MapPin,
  Cpu, Shield, Wifi, Printer, Monitor, ArrowRight, Star
} from 'lucide-react';

const PHONE = '0033626748299';
const PHONE_DISPLAY = '06 26 74 82 99';
const WHATSAPP = 'https://wa.me/33626748299';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const CtaBlock: React.FC = () => (
  <div className="bg-blue-600/10 border border-blue-500/30 rounded-2xl p-6 my-8 text-center">
    <p className="text-white font-semibold mb-1">Un problème informatique à Genève ?</p>
    <p className="text-gray-400 text-sm mb-4">Diagnostic gratuit — réponse rapide — intervention à distance.</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm">
        <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
      </a>
      <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </a>
    </div>
  </div>
);

const DepannageGeneve: React.FC = () => {
  useEffect(() => {
    document.title = 'Dépannage Informatique Genève (à distance) | SOS IT Services';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Dépannage informatique pour les habitants de Genève. Technicien basé près de la frontière, intervention à distance rapide. Tarifs abordables, diagnostic gratuit.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://sos-it-services.fr/depannage-informatique-geneve');

    const ogTags: Record<string, string> = {
      'og:title': 'Dépannage Informatique Genève (à distance) | SOS IT Services',
      'og:description': 'Technicien informatique basé près de Genève. Dépannage à distance pour les Genevois. Tarifs abordables, diagnostic gratuit.',
      'og:url': 'https://sos-it-services.fr/depannage-informatique-geneve',
      'og:image': 'https://sos-it-services.fr/images/depan-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#36393F] text-white">

      {/* Hero */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-b from-[#2F3136] to-[#36393F]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <span className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <MapPin className="w-4 h-4" /> Basé à côté de Genève · Service à distance
            </span>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" custom={1} variants={fadeUp}
            className="text-3xl md:text-5xl font-bold mb-6 leading-tight"
          >
            Dépannage informatique pour les habitants de{' '}
            <span className="text-blue-400">Genève</span>{' '}
            <span className="text-2xl md:text-3xl text-gray-400 font-normal">(à distance)</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="visible" custom={2} variants={fadeUp}
            className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed"
          >
            Vous habitez à Genève et votre ordinateur vous pose problème ?
            Notre technicien, basé juste de l'autre côté de la frontière en Haute-Savoie,
            intervient à distance rapidement. Pas de déplacement, pas d'attente —
            on règle votre panne depuis chez vous.
          </motion.p>

          <motion.div initial="hidden" animate="visible" custom={3} variants={fadeUp}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
                <Phone className="w-5 h-5" /> Appeler maintenant
              </a>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              {['Diagnostic gratuit', 'Intervention à distance', 'Satisfait ou remboursé'].map(badge => (
                <span key={badge} className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-400" /> {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contenu principal */}
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-16">

        {/* 1. Présentation */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Un technicien basé <span className="text-blue-400">à deux pas de Genève</span>
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>
              Notre technicien est installé en Haute-Savoie, à quelques kilomètres seulement de la frontière suisse.
              Nous connaissons bien le bassin genevois et ses habitants, souvent frontaliers ou binationaux,
              qui utilisent des ordinateurs avec des configurations mixtes (comptes Microsoft suisses,
              logiciels francophones, connexions à des serveurs en France et en Suisse).
            </p>
            <p>
              Pour les habitants de Genève, nous proposons exclusivement le <strong className="text-white">dépannage à distance</strong>.
              Cette formule est particulièrement adaptée : pas besoin de traverser la frontière,
              pas de frais de déplacement, et une intervention aussi rapide et efficace
              qu'une présence physique pour les problèmes logiciels.
            </p>
            <p>
              Nous parlons français, nous connaissons les spécificités des utilisateurs genevois,
              et nos tarifs sont en euros — souvent plus abordables que les prestataires locaux suisses.
            </p>
          </div>
          <CtaBlock />
        </motion.section>

        {/* 2. Pourquoi choisir un technicien français */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Pourquoi choisir un technicien français pour votre dépannage à Genève ?
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>
              Le coût de la vie à Genève est élevé, et les prestataires informatiques locaux le reflètent dans leurs tarifs.
              Un dépannage basique peut coûter entre 150 et 300 CHF chez un technicien genevois.
              Avec SOS IT Services, le tarif d'une intervention à distance commence à <strong className="text-white">50 €</strong> pour une intervention simple,
              sans frais de déplacement ni majoration transfrontalière.
            </p>
            <p>
              Le dépannage à distance ne connaît pas de frontières.
              Que vous soyez dans les Eaux-Vives, à Carouge, à Meyrin ou à Vernier —
              la connexion est identique, la qualité du service aussi.
            </p>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { icon: Headphones, label: 'Dépannage à distance', desc: 'Connexion sécurisée, vous voyez tout en temps réel' },
              { icon: Clock, label: 'Tarifs abordables', desc: 'À partir de 50 € — bien moins que le marché suisse' },
              { icon: CheckCircle, label: 'Satisfait ou remboursé', desc: 'Paiement uniquement si le problème est résolu' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-[#2F3136] rounded-xl p-4 border border-gray-700 text-center">
                <Icon className="w-7 h-7 text-blue-400 mx-auto mb-2" />
                <p className="text-white font-medium text-sm">{label}</p>
                <p className="text-gray-400 text-xs mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* 3. Problèmes traités */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Problèmes informatiques que nous résolvons à distance
          </h2>
          <div className="text-gray-300 leading-relaxed mb-6">
            <p>
              La majorité des pannes informatiques se règlent à distance en moins d'une heure.
              Voici les cas les plus fréquents parmi nos clients genevois :
            </p>
          </div>
          <div className="space-y-4">
            {[
              { icon: Cpu, title: 'Ordinateur lent', text: "Votre PC ou Mac rame, met du temps à démarrer, ou se bloque sur des tâches simples. Nettoyage, optimisation du démarrage et suppression des programmes inutiles — on récupère les performances perdues." },
              { icon: Shield, title: 'Virus et malwares', text: "Publicités intempestives, navigateur détourné, fichiers chiffrés — une infection peut vite devenir grave. Nous intervenons rapidement pour nettoyer votre système et sécuriser vos données." },
              { icon: Wifi, title: 'Connexion internet instable', text: "Coupures fréquentes, débit faible, réseau qui disparaît — nous diagnostiquons la cause et configurons correctement votre connexion depuis Genève." },
              { icon: Printer, title: 'Imprimante non reconnue', text: "Votre imprimante n'est plus visible après une mise à jour ? Nous réinstallons les pilotes et rétablissons la connexion en quelques minutes." },
              { icon: Monitor, title: 'Windows ou macOS bloqué', text: "Mises à jour bloquées, écran bleu, démarrage en boucle — nous stabilisons votre système sans perte de données." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-[#2F3136] rounded-xl p-5 border border-gray-700">
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <h3 className="font-semibold text-white">{title}</h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <CtaBlock />
        </motion.section>

        {/* 4. Comment ça marche */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-5">
            Comment fonctionne le dépannage à distance depuis Genève ?
          </h2>
          <div className="text-gray-300 leading-relaxed space-y-4">
            <p>
              Le processus est simple et ne nécessite aucune compétence technique de votre part.
            </p>
            <ol className="space-y-4 list-none">
              {[
                { num: '1', title: 'Vous nous contactez', text: "Par téléphone ou WhatsApp, vous décrivez votre problème en quelques mots. On vous répond rapidement pour convenir d'un créneau." },
                { num: '2', title: 'On installe un logiciel de connexion', text: "Nous vous guidons pour télécharger un petit logiciel sécurisé (TeamViewer ou équivalent). Cela prend moins de 2 minutes." },
                { num: '3', title: 'On prend le contrôle de votre ordinateur', text: "Vous voyez en temps réel ce que le technicien fait sur votre écran. Vous pouvez reprendre la main à tout moment. Rien n'est fait sans votre accord." },
                { num: '4', title: 'Le problème est résolu', text: "Une fois l'intervention terminée, le logiciel est fermé et la connexion coupée définitivement. Vous payez uniquement si le problème est résolu." },
              ].map(({ num, title, text }) => (
                <li key={num} className="flex gap-4">
                  <span className="w-8 h-8 rounded-full bg-blue-600/20 border border-blue-500/40 text-blue-400 font-bold flex items-center justify-center flex-shrink-0 text-sm">{num}</span>
                  <div>
                    <p className="text-white font-medium mb-1">{title}</p>
                    <p className="text-gray-400 text-sm">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </motion.section>

        {/* 5. Villes côté français */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} variants={fadeUp}>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Vous êtes côté français ? On intervient aussi à domicile
          </h2>
          <p className="text-gray-400 mb-6">
            Si vous habitez juste de l'autre côté de la frontière, dans le Genevois français,
            nous pouvons également nous déplacer à domicile en plus du service à distance.
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { name: 'Saint-Julien-en-Genevois', link: '/depannage-informatique-saint-julien-en-genevois' },
              { name: 'Annemasse', link: '/depannage-informatique-annemasse' },
              { name: 'Gex', link: '/depannage-informatique-gex' },
              { name: 'Ferney-Voltaire', link: '/depannage-informatique-ferney-voltaire' },
              { name: 'Saint-Genis-Pouilly', link: '/depannage-informatique-saint-genis-pouilly' },
              { name: 'Prévessin-Moëns', link: '/depannage-informatique-prevessin-moens' },
              { name: 'Viry', link: '/depannage-informatique-viry' },
              { name: 'Divonne-les-Bains', link: '/depannage-informatique-divonne-les-bains' },
            ].map(({ name, link }) => (
              <Link key={name} to={link} className="flex items-center gap-1.5 bg-[#2F3136] hover:bg-blue-600/20 border border-gray-700 hover:border-blue-500 text-gray-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors">
                <MapPin className="w-3.5 h-3.5 text-blue-400" /> {name}
              </Link>
            ))}
          </div>
          <div className="space-y-2">
            <Link to="/depannage-informatique-genevois" className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors">
              <ArrowRight className="w-4 h-4" /> Voir toutes nos zones d'intervention dans le Genevois
            </Link>
          </div>
        </motion.section>

      </div>

      {/* CTA final */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#2F3136] to-[#1e2124]">
        <div className="max-w-2xl mx-auto text-center">
          <Star className="w-8 h-8 text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Votre ordinateur pose problème à Genève ?
          </h2>
          <p className="text-gray-400 mb-2">
            Contactez-nous maintenant. Diagnostic gratuit, intervention à distance rapide,
            tarifs bien en dessous du marché suisse.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Technicien basé en Haute-Savoie, à quelques kilomètres de Genève.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
            </a>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DepannageGeneve;
